import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template/-private/handlebars';
import { buildWaiter } from '@ember/test-waiters';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import type { TaskInstance } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import set from 'ember-set-helper/helpers/set';
import { and, not, or } from 'ember-truth-helpers';
import config from 'garaje/config/environment';
import preventDefault from 'garaje/helpers/prevent-default';
import type ActiveStorageService from 'garaje/services/active-storage-extension';
import { StandaloneFileUpload } from 'garaje/utils/file-upload';
import { TrackedObject } from 'tracked-built-ins';
import { localCopy } from 'tracked-toolbox';
import type { FileUpload, ValidationResult } from 'utils/direct-uploader';

interface DirectUploaderComponentSignature {
  Args: {
    accept?: string;
    autoUpload?: boolean;
    currentUpload?: Partial<FileUpload> | null;
    directUploadURL?: string;
    disabled?: boolean;
    enqueue?: boolean;
    inputId?: string;
    invalidBody?: string;
    labelRetry?: string;
    invalidTitle?: string;
    label?: string;
    subLabel?: string;
    onError?: (error: unknown) => void;
    onFileSelected?: (file: File | null) => void;
    onFilePreview?: (file: FileReader['result']) => void;
    onPendingUpload?: (upload?: FileUpload) => void;
    onPreviewClick?: () => void;
    onReset?: () => void;
    onUpload?: (task: TaskInstance<string | undefined>) => void;
    onUploadComplete?: (signedId: string) => void;
    onValidate?: (result: ValidationResult) => void | boolean;
    prefix?: string;
    showResetButton?: boolean;
    showErrorState?: boolean;
    uploadProgress?: number;
    useStandaloneFileUpload?: boolean;
    /**
     * The maximum size of the file in bytes.
     */
    validSize?: number;
    validType?: string | RegExp;
  };
  Element: HTMLDivElement;
  Blocks: {
    uploading: [FileUpload | undefined];
    previewing: [FileUpload | undefined, () => void];
  };
}

const isTesting = config.environment === 'test';
const testWaiter = buildWaiter('direct-uploader-component-waiter');

export default class DirectUploader extends Component<DirectUploaderComponentSignature> {
  @service declare activeStorageExtension: ActiveStorageService;

  @localCopy('args.directUploadURL', '/a/visitors/api/direct-uploads') directUploadURL!: string;
  @localCopy('args.prefix', '') prefix!: string;
  @localCopy('args.currentUpload') currentUpload?: FileUpload;
  @localCopy('args.enqueue', true) enqueue!: boolean;
  @localCopy('args.showResetButton', true) showResetButton!: boolean;
  @localCopy('args.showErrorState', true) showErrorState!: boolean;

  fileInputElement?: HTMLInputElement;

  /**
   * Used to perform operations on the file input element when it disabled
   */
  @tracked forceEnable = false;

  get isImage(): boolean {
    return Boolean(this.args.accept?.includes('image'));
  }

  /**
   * disables the file input element
   */
  get disabled(): boolean {
    return (this.args.disabled || this.uploadIsRunning) && !this.forceEnable;
  }

  get shouldShowResetButton(): boolean {
    return Boolean(this.showResetButton && this.currentUpload && this.currentUpload.isValid);
  }

  get label(): string {
    if (this.args.label) return this.args.label;
    return `Drag and drop a file${this.formattedSize ? ' (max size of ' + this.formattedSize + ')' : ''}`;
  }

  get subLabel(): string {
    return this.args.subLabel ?? 'or select a file from your computer';
  }

  get progress(): number {
    return this.args.uploadProgress ?? this.currentUpload?.progress ?? 0;
  }

  get progressStyle(): SafeString {
    return htmlSafe(`width: ${this.progress}%; transition: width 200ms;`);
  }

  // "Reverse" progress bar gets narrower as upload proceeds
  get progressStyleImage(): SafeString {
    const remaining = 100 - this.progress;
    return htmlSafe(`width: ${remaining}%; transition: width 200ms;`);
  }

  get isInvalid(): boolean {
    return this.currentUpload?.isValid === false;
  }

  get retryText(): string {
    return this.args.labelRetry ?? this.args.label ?? 'Use another file';
  }

  get formattedSize(): string | undefined {
    if (!this.args.validSize) return undefined;
    return `${this.args.validSize / 1000000} mb`;
  }

  get uploadIsRunning(): boolean {
    if (this.useStandaloneFileUpload) {
      if (!this.currentUpload) return false;
      return !!(<StandaloneFileUpload>this.currentUpload).uploadTask?.isRunning;
    }
    return this.uploadTask.isRunning;
  }

  get useStandaloneFileUpload(): boolean {
    return this.args.useStandaloneFileUpload ?? false;
  }

  get validTypeReg(): RegExp | undefined {
    if (!this.args.validType) return undefined;
    if (this.args.validType instanceof RegExp) {
      return this.args.validType;
    }
    return new RegExp(this.args.validType);
  }

  @action
  fileSelected(files: FileList): void {
    this.forceEnable = false;

    if (!files?.length) return;

    const { autoUpload, onFileSelected, onFilePreview } = this.args;

    const file = files.item(0)!;
    const reader = new FileReader();

    let currentUpload: FileUpload;
    if (this.useStandaloneFileUpload) {
      const { directUploadURL, prefix } = this;
      currentUpload = new StandaloneFileUpload(file, {
        activeStorage: this.activeStorageExtension,
        directUploadURL,
        prefix,
      });
      currentUpload.isValid = this.validate(file);
      currentUpload.reset = () => this.reset();
    } else {
      currentUpload = new TrackedObject({
        file,
        upload: () => this.upload(file, currentUpload),
        reset: () => this.reset(),
        isValid: this.validate(file),
        uploaded: false,
      });
    }

    this.currentUpload = currentUpload;

    // do not proceed if file is invalid but keep context to inform user
    if (!currentUpload.isValid) return;

    const token = isTesting ? testWaiter.beginAsync() : false;

    reader.onload = () => {
      const { result } = reader;

      currentUpload.readerResult = <string>result;

      onFilePreview?.(result);

      if (token) testWaiter.endAsync(token);
    };
    reader.readAsDataURL(file);

    onFileSelected?.(file);
    // reset input value now that file has been consumed to allow the same file to be selected if the upload is ever cleared
    if (this.fileInputElement) this.fileInputElement.value = '';

    if (autoUpload) {
      void this.currentUpload.upload();
    } else {
      // upload is deferred and handler by consuming context
      this.args.onPendingUpload?.(currentUpload);
    }
  }

  upload(file: File, uploadContext: FileUpload): TaskInstance<string | undefined> {
    const { directUploadURL, prefix } = this;

    // ember-active-storage addon does not support extra params in the POST payload.
    // But API currently supports sending the prefix as a query param.
    const endpoint = prefix ? `${directUploadURL}?prefix=${prefix}` : directUploadURL;
    const task = this.uploadTask.perform(file, endpoint, uploadContext);

    this.args.onUpload?.(task);
    return task;
  }

  uploadTask = task({ enqueue: this.enqueue }, async (file: File, endpoint: string, uploadContext: FileUpload) => {
    if (!file) {
      throw new Error('Upload halted: no file specified');
    }

    if (!endpoint) {
      throw new Error('Upload halted: no direct upload endpoint specified');
    }

    if (typeof this.activeStorageExtension.upload !== 'function') {
      throw new Error('Upload halted: invalid Active Storage service specified');
    }

    try {
      const { signedId } = await this.activeStorageExtension.upload(file, endpoint, {
        onProgress: (progress) => {
          uploadContext.progress = progress;
        },
      });

      uploadContext.uploaded = true;
      this.args.onUploadComplete?.(signedId);

      return signedId;
    } catch (error) {
      this.args.onError?.(error);
      return;
    }
  });

  @action
  reset(): void {
    this.currentUpload = undefined;

    this.args.onFileSelected?.(null);
    this.args.onFilePreview?.(null);
    this.args.onPendingUpload?.(undefined);
    this.args.onReset?.();
  }

  @action
  forceUpload(): void {
    this.forceEnable = true;
    // eslint-disable-next-line ember/no-runloop
    schedule('afterRender', () => this.fileInputElement?.click());
  }

  @action
  onPreviewClick(): void {
    this.args.onPreviewClick?.();
  }

  validate(file: File): boolean {
    if (!file) return false;

    const typeValid = this.validTypeReg ? this.validTypeReg.test(file.type) : true;
    const sizeValid = this.args.validSize ? file.size <= this.args.validSize : true;

    this.args.onValidate?.({ typeValid, sizeValid });

    return typeValid && sizeValid;
  }

  <template>
    <div
      class="relative border border-dashed flex flex-col px-6 py-4 justify-center items-center rounded-md
        {{if (and this.isImage this.currentUpload.isValid) 'direct-uploader file-selected !p-0'}}"
      data-test-direct-uploader
      data-test-uploader-progress={{this.progress}}
      ...attributes
    >
      <input
        {{didInsert (set this "fileInputElement")}}
        data-test-file-input
        data-test-invalid-file-input={{this.isInvalid}}
        class="absolute !w-full !h-full opacity-0 {{if this.disabled 'cursor-auto -z-10' 'cursor-pointer'}}"
        type="file"
        accept={{@accept}}
        disabled={{this.disabled}}
        id={{or @inputId "file-upload"}}
        {{on "change" (pick "target.files" this.fileSelected)}}
        {{on "drop" (preventDefault (pick "dataTransfer.files" this.fileSelected))}}
        {{on "cancel" (set this "forceEnable" false)}}
      />
      {{#if (and this.isInvalid this.showErrorState)}}
        {{! error state }}
        <img
          src="/assets/images/circle-exclamation-red.svg"
          alt="something went wrong"
          class="align-middle relative bg-white rounded-full"
          style="min-width: 24px; width:24px; height:24px;"
        />
        <label for="file-upload" class="pt-2 !text-lg text-center" data-test-invalid-message>
          {{if @invalidTitle @invalidTitle "File is invalid"}}
          {{#if @invalidBody}}
            <span class="text-center text-grey block !text-base">{{htmlSafe @invalidBody}}</span>
          {{/if}}
        </label>
        <button type="button" class="btn--link text-red-50 text-base">
          {{this.retryText}}
        </button>
      {{else if (and (or this.uploadIsRunning this.progress) (not this.currentUpload.uploaded))}}
        {{! file is uploading }}
        {{#if (has-block "uploading")}}
          {{yield this.currentUpload to="uploading"}}
        {{else}}
          {{#if this.isImage}}
            {{#if this.currentUpload.readerResult}}
              <img
                src={{this.currentUpload.readerResult}}
                alt="Preview"
                class="object-contain w-full h-full z-10"
                data-private
                data-test-preview
                {{on "click" this.onPreviewClick}}
              />
            {{/if}}
            <div
              class="absolute h-full top-0 right-0 bg-white opacity-25"
              style={{this.progressStyleImage}}
              data-test-direct-uploader-progress
            ></div>
          {{else}}
            <label for="file-upload" class="!text-base">Uploading file: {{this.currentUpload.file.name}}</label>
            <div class="flex items-center">
              <span class="w-80 h-2 bg-carbon-30 rounded mr-2 relative">
                <span
                  class="h-full absolute bg-gem-50 rounded"
                  style={{this.progressStyle}}
                  data-test-upload-progress
                ></span>
              </span>
              <span>{{this.progress}}%</span>
            </div>
          {{/if}}
        {{/if}}
      {{else if this.currentUpload.isValid}}
        {{! file is in a preview state }}
        {{#if (has-block "previewing")}}
          {{yield this.currentUpload this.forceUpload to="previewing"}}
        {{else}}
          {{#if this.isImage}}
            {{#if this.currentUpload.readerResult}}
              <img
                src={{this.currentUpload.readerResult}}
                alt="Preview"
                class="object-contain w-full h-full {{if @onPreviewClick 'z-10'}}"
                data-private
                data-test-preview
                {{on "click" this.onPreviewClick}}
              />
            {{/if}}
          {{else}}
            <label for="file-upload" class="!text-base" data-test-has-file>{{this.currentUpload.file.name}}</label>
            <button type="button" class="btn--link !text-gem-50 text-base">Change file</button>
          {{/if}}
        {{/if}}
      {{else}}
        {{! nothing has been selected }}
        <label for="file-upload" class="{{if @disabled 'opacity-50'}} !text-base">{{this.label}}</label>
        <button
          type="button"
          class="{{if @disabled 'opacity-50' 'btn--link'}} !text-gem-50 text-base"
        >{{this.subLabel}}</button>
      {{/if}}
    </div>

    {{#if this.shouldShowResetButton}}
      <div class="flex justify-center items-center h-16 z-10">
        <button
          class="btn btn--small btn--outlineFill"
          disabled={{this.uploadTask.isRunning}}
          type="button"
          {{on "click" this.reset}}
          data-test-reset
        >
          {{#if this.uploadTask.isRunning}}
            Uploading:
            {{this.progress}}%
          {{else}}
            Clear photo
          {{/if}}
        </button>
      </div>
    {{/if}}
  </template>
}
